'use client';

import { useEffect, useId, useMemo, useState } from 'react';
import { useCart } from '@akademi/site-builder-runtime';
import { unitName, type CabinetCatalogue, type CabinetGroup, type CabinetProduct } from '../data/cabinetCatalogue';
import { formatGbp } from '../data/doorPricing';
import { cabinetSvg } from '../drawing/cabinet';
import { SvgArt } from '../drawing/SvgArt';

/**
 * The shop's standard cabinets, by the same groups the kitchen planner's
 * picker uses, each with this shopper's price and a real "Add".
 *
 * <p>Laid out as the site mockup's catalogue (`cabinets.html`): a filter
 * column (unit type, then width), a toolbar with the count and sort, and the
 * grid. Everything shown is read from the shop: the published carcass range
 * and the price list. A unit with no price is shown as "Not sold online" and
 * has no basket button.
 */
export interface StandardCabinetsProps {
  catalogue: CabinetCatalogue;
  /** A group id without its range prefix (`base`, `wall`…), from `?type=`. */
  initialGroup?: string;
  /** Door colour to draw the units in. */
  colour: string;
  onCustom(): void;
}

type Sort = 'width' | 'price-asc' | 'price-desc';

/** Cards shown before "Show more". */
const PAGE = 24;
/** The "Can't find your size?" panel sits after this many cards, as in the mockup. */
const CUSTOM_AFTER = 6;

/** Kitchen units first, in the order a kitchen is planned; bedroom last. */
const PARENT_ORDER = ['Base Units', 'Wall Units', 'Tall Units', 'Corner Base Units', 'Corner Wall Units', 'Appliance Housings'];

function parentRank(name: string, section: string): number {
  const at = PARENT_ORDER.indexOf(name);
  if (at >= 0) return at;
  return section === 'bedroom' ? PARENT_ORDER.length + 1 : PARENT_ORDER.length;
}


function drawing(p: CabinetProduct, colour: string, idPrefix: string): string {
  const type = p.opening === 'drawers' ? 'drawers' : p.category === 'wardrobe' ? 'tall' : p.category;
  const doors = p.fronts.reduce((s, f) => s + f.quantity, 0);
  // Two door heights on one unit are a tall unit's upper and lower doors, one row of each.
  const heights = [...new Set(p.fronts.map((f) => f.height))];
  const stacked = p.opening === 'door' && heights.length === 2 && p.category === 'tall';
  const across = stacked ? Math.max(1, Math.round(doors / 2)) : doors;
  return cabinetSvg({
    type,
    w: p.w,
    h: p.h,
    d: p.d,
    doors: p.opening === 'door' && across > 0 ? Math.min(across, 4) : undefined,
    upper: stacked ? Math.min(...heights) : undefined,
    drawers: p.opening === 'drawers' ? Math.max(1, Math.min(doors, 5)) : undefined,
    open: p.opening === 'none',
    shelves: p.opening === 'none' ? 2 : 1,
    colour,
    label: unitName(p.name),
    idPrefix,
  });
}

function sorted(products: CabinetProduct[], sort: Sort): CabinetProduct[] {
  if (sort === 'width') return products;
  const price = (p: CabinetProduct) => (p.price === undefined ? Number.POSITIVE_INFINITY : p.price);
  const out = [...products].sort((a, b) => price(a) - price(b) || a.w - b.w);
  if (sort === 'price-desc') {
    // Unpriced units stay last either way.
    const priced = out.filter((p) => p.price !== undefined).reverse();
    return [...priced, ...out.filter((p) => p.price === undefined)];
  }
  return out;
}

export function StandardCabinets({ catalogue, initialGroup, colour, onCustom }: StandardCabinetsProps) {
  const id = useId();
  const cart = useCart();
  const [groups, setGroups] = useState<CabinetGroup[] | null>(null);
  const [group, setGroup] = useState<string | null>(null);
  const [products, setProducts] = useState<CabinetProduct[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const [widths, setWidths] = useState<ReadonlySet<number>>(new Set());
  const [sort, setSort] = useState<Sort>('width');
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    let current = true;
    catalogue
      .groups()
      .then((list) => {
        if (!current) return;
        setGroups(list);
        const wanted = list.find((g) => g.id.endsWith(`:${initialGroup ?? 'base'}`)) ?? list.find((g) => g.section !== 'bedroom') ?? list[0];
        setGroup(wanted?.id ?? null);
      })
      .catch(() => current && setError('The cabinet range could not be loaded. Please try again in a moment.'));
    return () => {
      current = false;
    };
  }, [catalogue, initialGroup]);

  useEffect(() => {
    if (!group) return;
    let current = true;
    setProducts(null);
    setWidths(new Set());
    setShown(PAGE);
    catalogue
      .products(group)
      .then((list) => current && setProducts(list))
      .catch(() => current && setError('These cabinets could not be loaded. Please try again in a moment.'));
    return () => {
      current = false;
    };
  }, [catalogue, group]);

  const parents = useMemo(() => {
    const out = new Map<string, { section: string; groups: CabinetGroup[] }>();
    for (const g of groups ?? []) {
      const key = g.parentName ?? g.name;
      const entry = out.get(key) ?? { section: g.section, groups: [] };
      entry.groups.push(g);
      out.set(key, entry);
    }
    return [...out.entries()].sort(([a, x], [b, y]) => parentRank(a, x.section) - parentRank(b, y.section));
  }, [groups]);
  const active = groups?.find((g) => g.id === group);
  const activeParent = active ? (active.parentName ?? active.name) : undefined;

  const widthCounts = useMemo(() => {
    const out = new Map<number, number>();
    for (const p of products ?? []) out.set(p.w, (out.get(p.w) ?? 0) + 1);
    return [...out.entries()].sort(([a], [b]) => a - b);
  }, [products]);
  const visible = useMemo(
    () => sorted((products ?? []).filter((p) => widths.size === 0 || widths.has(p.w)), sort),
    [products, widths, sort],
  );

  function toggleWidth(w: number) {
    setShown(PAGE);
    setWidths((now) => {
      const next = new Set(now);
      if (next.has(w)) next.delete(w);
      else next.add(w);
      return next;
    });
  }

  async function add(p: CabinetProduct) {
    if (!cart || p.productId === undefined) return;
    const ok = await cart.add({ productId: p.productId, quantity: '1' });
    setAdded(ok ? `${unitName(p.name)}, ${p.w}mm is in your basket.` : (cart.error ?? 'That did not go into your basket. Please try again.'));
  }

  if (error) {
    return (
      <p className="sk-db__notice" role="alert">
        {error}
      </p>
    );
  }
  if (!groups) return <p className="sk-cb__note">Loading the cabinet range…</p>;

  const custom = (
    <li key="custom" className="sk-cb__custom">
      <div>
        <h3 className="sk-heading">Can’t find your size?</h3>
        <p>Specify the width, height and depth you need, and the doors and fronts it takes.</p>
      </div>
      <button type="button" className="sk-btn sk-btn--brass" onClick={onCustom}>
        Make one to measure
      </button>
    </li>
  );
  const cards = visible.slice(0, shown).map((p) => (
    <li key={p.sku} className="sk-cb__product">
      <div className="sk-cb__pic">
        <SvgArt svg={drawing(p, colour, `${id}${p.sku}`)} />
      </div>
      <div className="sk-cb__pbody">
        <h3>{`${unitName(p.name)}, ${p.w}mm`}</h3>
        <span className="sk-cb__spec">{`${p.w}w × ${p.h}h × ${p.d}d mm${p.legHeight ? ` · on ${p.legHeight}mm legs` : ''}`}</span>
        {p.fronts.length > 0 && (
          <span className="sk-cb__spec">{`Takes ${p.fronts.map((f) => `${f.quantity > 1 ? `${f.quantity} × ` : ''}${f.height} × ${f.width}`).join(' + ')} mm fronts`}</span>
        )}
        <div className="sk-cb__buy">
          {p.available && p.price !== undefined ? (
            <>
              <b>
                {formatGbp(p.price)} <small>carcass, inc. VAT</small>
              </b>
              {cart?.available && (
                <button type="button" className="sk-btn sk-btn--primary sk-btn--sm" disabled={cart.busy} onClick={() => add(p)}>
                  Add
                  <span className="sk-visually-hidden">{` ${unitName(p.name)}, ${p.w}mm to basket`}</span>
                </button>
              )}
            </>
          ) : (
            <span className="sk-cb__spec">Not sold online</span>
          )}
        </div>
      </div>
    </li>
  ));
  cards.splice(Math.min(CUSTOM_AFTER, cards.length), 0, custom);

  return (
    <div className="sk-cb__shop">
      <aside className="sk-cb__filters" aria-label="Filters">
        <div className="sk-cb__fgroup">
          <h3 id={`${id}-types`}>Unit type</h3>
          <ul className="sk-cb__types-nav" aria-labelledby={`${id}-types`}>
            {parents.map(([name, entry]) => (
              <li key={name}>
                <button type="button" aria-pressed={name === activeParent} onClick={() => setGroup(entry.groups[0]!.id)}>
                  <span>{name}</span>
                  <span className="sk-cb__count">{entry.groups.reduce((s, g) => s + g.count, 0)}</span>
                </button>
                {name === activeParent && entry.groups.length > 1 && (
                  <ul className="sk-cb__children" aria-label={name}>
                    {entry.groups.map((g) => (
                      <li key={g.id}>
                        <button type="button" aria-pressed={g.id === group} onClick={() => setGroup(g.id)}>
                          <span>{g.name}</span>
                          <span className="sk-cb__count">{g.count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
        {widthCounts.length > 1 && (
          <div className="sk-cb__fgroup" role="group" aria-labelledby={`${id}-widths`}>
            <h3 id={`${id}-widths`}>Width</h3>
            <div className="sk-cb__widths">
              {widthCounts.map(([w, n]) => (
                <label key={w} className="sk-cb__check">
                  <input type="checkbox" checked={widths.has(w)} onChange={() => toggleWidth(w)} />
                  <span>{`${w}mm`}</span>
                  <span className="sk-cb__count">{n}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </aside>

      <div>
        <div className="sk-cb__toolbar">
          <p>
            {products ? (
              <>
                <b>{visible.length}</b>
                {` ${active?.name ?? 'cabinets'} · carcass prices inc. VAT`}
              </>
            ) : (
              'Loading cabinets…'
            )}
          </p>
          <select className="sk-db__input sk-db__mselect" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="width">Width</option>
            <option value="price-asc">Price: low to high</option>
            <option value="price-desc">Price: high to low</option>
          </select>
        </div>
        <p className="sk-visually-hidden" role="status">
          {added ?? ''}
        </p>
        {added && (
          <p className="sk-cb__note" aria-hidden="true">
            {added}
          </p>
        )}
        {products && (
          <>
            <ul className="sk-cb__products" aria-label={active?.name}>
              {cards}
            </ul>
            {visible.length > shown && (
              <div className="sk-cb__more">
                <button type="button" className="sk-btn sk-btn--outline" onClick={() => setShown((n) => n + PAGE)}>
                  {`Show more (${visible.length - shown} left)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
